import { RoundedToolbar } from "./RoundedToolbar"
import { IconButton, Slider, styled } from "@mui/material"
import { Expr, type Color } from "../../shared/Expr"
import { ForwardedRef, forwardRef, ReactNode, useState } from "react"
import { PopoverButton } from "./PopoverButton"
import { colors } from "./ColorPicker"
import Colorize from "@mui/icons-material/Colorize"
import { ColorSelectorGrid } from "../ui/ColorSelectorGrid"
import { ToolButton } from "./ToolButton"
import { StateNode, useEditor, useValue } from "tldraw"
import { GeoShapeGeoStyle } from "../../shared/LDGeoShape"
import { Circle, Square } from "lucide-react"
import { LineIcon } from "../icons/Line"
import { ToolbarIconButton } from "./Toolbar"
import { useCurrentPage } from "../hooks/useCurrentPage"
import { setCursor } from "../components/CustomCursor"

// only available in chrome
const eyeDropper = "EyeDropper" in window ? new EyeDropper() : null

type Props = {
  fillColor: Color
  strokeColor: Color
  recentFillColors: Color[]
  recentStrokeColors: Color[]
  markerRadius: number
  strokeWidth: number
  onFillColorChange: (c: Color) => void
  onStrokeColorChange: (c: Color) => void
  onMarkerRadiusChange: (radius: number) => void
  onPencilRadiusChange: (radius: number) => void
}

export function ColorToolsToolbar({
  fillColor,
  strokeColor,
  recentFillColors,
  recentStrokeColors,
  markerRadius,
  strokeWidth,
  onFillColorChange,
  onStrokeColorChange,
  onMarkerRadiusChange,
  onPencilRadiusChange,
}: Props) {
  const editor = useEditor()

  const page = useCurrentPage()

  const layerOverrides = page.meta.layers[page.meta.currentLayer].layerOverrides

  const currentTool = useValue("currentTool", () => editor.getCurrentTool(), [
    editor,
  ])

  const childTool = useValue("childTool", () => currentTool.getCurrent(), [
    currentTool,
    editor,
  ]) as StateNode & { fill: boolean }

  const geoStyle = useValue(
    "geoStyle",
    () => editor.getStyleForNextShape(GeoShapeGeoStyle),
    [editor],
  )
  const geoPopoverOpen =
    currentTool.id === "custom-geo" || currentTool.id === "custom-line"

  const strokeColorOverride =
    layerOverrides.strokeColor &&
    Expr.evaluate(layerOverrides.strokeColor, page.meta.parameterValues)

  const strokeWidthOverride =
    layerOverrides.strokeWidth &&
    Expr.evaluate(layerOverrides.strokeWidth, page.meta.parameterValues)
  const fillColorOverride =
    layerOverrides.fillColor &&
    Expr.evaluate(layerOverrides.fillColor, page.meta.parameterValues)

  // Unlocks the shapes that are locked by the "onlySelectedLayer" setting
  // This might only be necessary for bucket fill but i'm not sure
  function unlockShapes() {
    const unlockedShapes = editor
      .getCurrentPageShapes()
      .filter((shape) => shape.parentId === page.id)
      .map((shape) => ({ ...shape, isLocked: false }))
    editor.updateShapes(unlockedShapes)
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
      <RoundedToolbar direction="vertical">
        <ColorPickerButton
          color={strokeColorOverride ?? strokeColor}
          onChange={onStrokeColorChange}
          recentColors={recentStrokeColors}
        >
          {(clickHandler) => (
            <ColorPickerButtonTrigger
              onClick={clickHandler}
              disabled={strokeColorOverride !== null}
              style={{
                background: strokeColorOverride ?? strokeColor,
              }}
            />
          )}
        </ColorPickerButton>
        <ToolButton
          tooltip="Pencil"
          tooltipPlacement="left"
          icon="pencil"
          selected={currentTool.id === "draw-stroke"}
          onClick={() => {
            unlockShapes()
            editor.setCurrentTool("draw-stroke")
          }}
        >
          {" "}
          <Slider
            size="small"
            orientation="vertical"
            valueLabelDisplay="auto"
            value={strokeWidthOverride ?? strokeWidth}
            disabled={strokeWidthOverride !== null}
            min={0.1}
            max={20}
            step={0.1}
            onChange={(_, value) => onPencilRadiusChange(value)}
            sx={{ height: 74 }}
          />
        </ToolButton>
        <ToolButton
          tooltip="Arrow"
          tooltipPlacement="left"
          icon="arrow"
          selected={currentTool.id === "arrow2"}
          onClick={() => {
            unlockShapes()
            editor.setCurrentTool("arrow2")
          }}
        />
        <ToolButton
          tooltip="Text"
          tooltipPlacement="left"
          icon="text"
          selected={currentTool.id == "text2"}
          onClick={() => {
            unlockShapes()
            editor.setCurrentTool("text2")
          }}
        />
        <ToolButton
          tooltip=""
          icon={
            geoStyle === "rectangle"
              ? "square"
              : geoStyle === "ellipse"
                ? "circle"
                : "line"
          }
          selected={
            (currentTool.id === "custom-geo" && childTool.fill === false) ||
            currentTool.id === "custom-line"
          }
          onClick={() => {
            unlockShapes()
            if (editor.getStyleForNextShape(GeoShapeGeoStyle) === "cloud") {
              editor.setCurrentTool("custom-line")
            } else {
              editor.setCurrentTool("select")
              editor.setCurrentTool("custom-geo", { fill: false })
            }
          }}
        />
      </RoundedToolbar>

      <RoundedToolbar direction="vertical">
        <ColorPickerButton
          color={fillColorOverride ?? fillColor}
          onChange={onFillColorChange}
          recentColors={recentFillColors}
        >
          {(clickHandler) => (
            <ColorPickerButtonTrigger
              onClick={clickHandler}
              disabled={fillColorOverride !== null}
              style={{
                background: fillColorOverride ?? fillColor,
              }}
            />
          )}
        </ColorPickerButton>
        <ToolButton
          tooltip="Marker"
          tooltipPlacement="left"
          icon="marker"
          selected={currentTool.id === "marker"}
          onClick={(e) => {
            unlockShapes()
            setCursor(editor, "circle", "marker")
            editor.setCursor({
              type: "none",
              rotation: 0,
            })
            editor.setCurrentTool("marker")
          }}
        >
          <Slider
            size="small"
            orientation="vertical"
            valueLabelDisplay="auto"
            value={markerRadius}
            min={2}
            max={30}
            step={1}
            onChange={(_, value) => onMarkerRadiusChange(value)}
            sx={{ height: 74 }}
          />
        </ToolButton>
        <ToolButton
          tooltip="Draw Fill"
          tooltipPlacement="left"
          icon="fill"
          selected={currentTool.id == "draw-fill"}
          onClick={() => {
            unlockShapes()
            editor.setCurrentTool("draw-fill")
          }}
        />
        <ToolButton
          tooltip="Bucket Fill"
          tooltipPlacement="left"
          icon="bucket"
          selected={currentTool.id == "bucket"}
          onClick={() => {
            unlockShapes()
            editor.setCurrentTool("bucket")
          }}
        />
        <ToolButton
          tooltip="Shape Fill"
          tooltipPlacement="left"
          icon={
            geoStyle === "rectangle"
              ? "square"
              : geoStyle === "ellipse"
                ? "circle"
                : "square"
          }
          selected={currentTool.id === "custom-geo" && childTool.fill === true}
          onClick={() => {
            if (geoStyle === "cloud") {
              editor.setStyleForNextShapes(GeoShapeGeoStyle, "rectangle")
            }
            editor.setCurrentTool("custom-geo.fill", { fill: true })
          }}
        />
      </RoundedToolbar>
      {geoPopoverOpen && (
        <div
          style={{
            position: "absolute",
            right: 50,

            top: childTool.fill ? 345 : 75,
          }}
        >
          <RoundedToolbar direction="vertical">
            <ToolButton
              tooltip=""
              icon="square"
              selected={geoStyle === "rectangle"}
              onClick={() => {
                editor.setStyleForNextShapes(GeoShapeGeoStyle, "rectangle")
                editor.setCurrentTool("custom-geo")
              }}
            />
            <ToolButton
              tooltip=""
              icon="circle"
              selected={geoStyle === "ellipse"}
              onClick={() => {
                editor.setStyleForNextShapes(GeoShapeGeoStyle, "ellipse")
                editor.setCurrentTool("custom-geo")
              }}
            />
            {!childTool.fill && (
              <ToolButton
                tooltip=""
                icon="line"
                selected={currentTool.id === "custom-line"}
                onClick={() => {
                  editor.setCurrentTool("custom-line")
                  editor.setStyleForNextShapes(GeoShapeGeoStyle, "cloud")
                }}
              />
            )}
          </RoundedToolbar>
        </div>
      )}
    </div>
  )
}

const ColorPickerButtonTrigger = styled("button")({
  borderRadius: "999px",
  width: 30,
  height: 30,
  cursor: "pointer",
  boxShadow: "1px 1px 3px rgba(0,0,0,0.2)",
  marginBottom: "8px",
  transform: "scale(1.3)",
  outline: "3px solid white",
  border: "0.5px solid rgba(0,0,0,0.2)",
})

type ColorPickerButtonProps<T extends HTMLElement> = {
  children: (
    clickHandler: (event: React.MouseEvent<T>) => void,
    ref: ForwardedRef<HTMLButtonElement>,
    color: Color,
    isActive: boolean,
  ) => ReactNode
  color: Color
  onChange: (color: Color) => void
  recentColors: Color[]
}

export const ColorPickerButton = forwardRef<
  HTMLButtonElement,
  ColorPickerButtonProps<HTMLButtonElement>
>(function ColorPickerButton({ color, onChange, children, recentColors }, ref) {
  if (!recentColors) throw `No recent colors!`
  return (
    <PopoverButton
      ref={ref}
      renderButton={(clickHandler, ref, isOpen) =>
        children(clickHandler, ref, color, isOpen)
      }
      anchorOrigin={{ vertical: "top", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
    >
      {(close) => (
        <div
          style={{
            padding: "4px",
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          <ColorSelectorGrid
            colors={recentColors}
            selectedColor={color}
            colorsPerRow={11}
            onSelect={(color) => {
              onChange(color)
              close()
            }}
          />

          <ColorSelectorGrid
            colors={colors.map((c) => c.color as Color)}
            selectedColor={color}
            colorsPerRow={11}
            onSelect={(color) => {
              onChange(color)
              close()
            }}
          />

          {eyeDropper && (
            <div>
              <IconButton
                size="small"
                onClick={async () => {
                  const res = await eyeDropper.open()
                  onChange(res.sRGBHex as Color)
                  close()
                }}
              >
                <Colorize />
              </IconButton>
            </div>
          )}
        </div>
      )}
    </PopoverButton>
  )
})
